import React from "react";
import InputMask from "react-input-mask";

export default React.memo(function Field({
  f,
  raw,
  inDocx,
  val,
  isRO,
  showPlaceholders,
  registerRef,
  update,
  onFieldFocus,
  onFieldChangeKey,
}) {
  // Regex sécurisée
  let validateRegex = null;
  if (f.regex_validation) {
    try {
      validateRegex = new RegExp(f.regex_validation);
    } catch (e) {
      console.warn(
        `Regex invalide pour le champ ${f.key}:`,
        f.regex_validation,
        e.message
      );
      validateRegex = null;
    }
  }

  const handleChange = (e) => {
    if (isRO) return;

    // Cas particulier : checkbox avec template_if_checked / template_if_unchecked
    if (f.type === "checkbox") {
      const checked = e.target.checked;

      const checkedTpl =
        (f.template_if_checked && String(f.template_if_checked)) || "Oui";
      const uncheckedTpl =
        (f.template_if_unchecked && String(f.template_if_unchecked)) || "";

      const finalVal = checked ? checkedTpl : uncheckedTpl;

      update(f.key, finalVal);
      if (onFieldChangeKey) onFieldChangeKey(f.key, finalVal);
      return;
    }

    // Cas normal (text, textarea, number, date, select, ...)
    const newVal = e.target.value;
    if (validateRegex && newVal && !validateRegex.test(newVal)) {
      console.warn(`Valeur invalide pour ${f.key}:`, newVal);
    }
    update(f.key, newVal);
    if (onFieldChangeKey) onFieldChangeKey(f.key, newVal);
  };

  return (
    <div
      className={`form-group ${isRO ? "readonly" : ""}`}
      onFocusCapture={() => onFieldFocus && onFieldFocus(f.key)}
    >
      <label className="form-label">
        {f.label}
        {f.required && " *"} {isRO && <span title="Lecture seule">[lock]</span>}
        {f.tooltip && (
          <span className="tooltip-icon" title={f.tooltip} style={{ marginLeft: 6 }}>
            [info]
          </span>
        )}
        {showPlaceholders && (
          <span className="placeholder-tag">
            {f.type === "image" ? ` ({%${f.key}})` : ` ({${f.key}})`}
          </span>
        )}
        {/* [ok] Nouvelle indication */}
        {!inDocx && (
          <span
            style={{
              fontSize: "0.8em",
              color: "#d9534f",
              backgroundColor: "#f9d6d5",
              padding: "2px 6px",
              borderRadius: "6px",
            }}
          >
            [warning]
          </span>
        )}
      </label>

      {/* textarea */}
      {f.type === "textarea" && (
        <textarea
          value={val || ""}
          onChange={handleChange}
          ref={(el) => registerRef(f.key, el)}
          rows={4}
          className="form-textarea"
          placeholder={f.placeholder}
          readOnly={isRO}
          maxLength={f.max_length || undefined}
        />
      )}

      {/* select */}
      {f.type === "select" && (
        <select
          ref={(el) => registerRef(f.key, el)}
          className="form-select"
          value={
            f.multi_select
              ? (() => {
                  const arr = Array.isArray(val) ? val : [];
                  if (!f.optionsMap) return arr;
                  // map valeurs -> labels
                  return arr.map(v => {
                    const entry = Object.entries(f.optionsMap).find(([, vv]) => vv === v);
                    return entry ? entry[0] : v;
                  });
                })()
              : (() => {
                  if (!f.optionsMap) return (val === "" ? "" : String(val));
                  const entry = Object.entries(f.optionsMap).find(([, v]) => v === (val ?? ""));
                  return entry ? entry[0] : "";
                })()
          }
          onChange={(e) => {
            if (f.multi_select) {
              const selectedLabels = Array.from(e.target.selectedOptions).map(opt => opt.value);
              const selectedValues = f.optionsMap
                ? selectedLabels.map(lbl => f.optionsMap[lbl] ?? lbl)
                : selectedLabels;
              update(f.key, selectedValues);
            } else {
              const selectedLabel = e.target.value;
              const newVal = f.optionsMap ? (f.optionsMap[selectedLabel] ?? "") : selectedLabel;
              update(f.key, newVal);
            }
          }}
          disabled={isRO}
          multiple={f.multi_select} // [ok] nouvelle prop
        >
          {!f.multi_select && <option value="">- Sélectionner -</option>}
          {(f.options || []).map(lbl => (
            <option key={`${f.key}:${lbl}`} value={lbl}>
              {lbl}
            </option>
          ))}
        </select>
      )}  

      {/* checkbox */}
      {f.type === "checkbox" && (
        <input
          type="checkbox"
          checked={
            raw === (f.template_if_checked || "Oui") ||
            raw === true // pour compatibilité d'anciens brouillons
          }
          onChange={handleChange}
          ref={(el) => registerRef(f.key, el)}
          disabled={isRO}
        />
      )}
      {/* text/number/date avec mask */}
      {f.type === "text" && (
        <>          
          {typeof f.mask === "string" && f.mask.trim() ? (
            <InputMask
              mask={f.mask.trim()}
              value={val || ""}
              onChange={handleChange}
              maskChar={null}
              alwaysShowMask={false}
              readOnly={isRO}
              placeholder={f.placeholder}
              ref={(el) => registerRef(f.key, el)}
            >
              {(inputProps) => (
                <input
                  {...inputProps}
                  type="text"
                  className="form-input"
                  maxLength={f.max_length || undefined}
                />
              )}
            </InputMask>
           ) : (
            <input
              type="text"
              value={val || ""}
              onChange={handleChange}
              ref={(el) => registerRef(f.key, el)}
              className="form-input"
              placeholder={f.placeholder}
              readOnly={isRO}
              maxLength={f.max_length || undefined}
            />
          )}
        </>
      )}
      
      {["number", "date"].includes(f.type) && (
       <input
         type={f.type}
         value={val || ""}
         onChange={handleChange}
         ref={(el) => registerRef(f.key, el)}
         className="form-input"
         placeholder={f.placeholder}
         readOnly={isRO}
         maxLength={f.max_length || undefined}
       />
      )}     

      {/* help_text */}
      {f.help_text && <small className="form-help">{f.help_text}</small>}

      {/* message d'erreur regex */}
      {validateRegex && val && !validateRegex.test(val) && (
        <small className="form-error" style={{ color: "red" }}>
          Format invalide
        </small>
      )}
      {/* image */}
      {f.type === "image" && (
        <div>
          <input
            type="file"
            accept="image/*"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const ab = await file.arrayBuffer();
              const bytes = new Uint8Array(ab);
              const prev = (val && typeof val === "object") ? val : {};
              // On conserve wmm/hmm existants si déjà saisis
              const next = {
                ...prev,
                bytes,
                fileName: file.name,
                wmm: prev.wmm ?? 40,
                hmm: prev.hmm ?? 20,
              };
              update(f.key, next);
            }}
            ref={(el) => registerRef(f.key, el)}
            className="form-input"
            disabled={isRO}
          />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 6 }}>
            <div>
              <label className="form-label">Largeur (mm)</label>
              <input
                type="number"
                className="form-input"
                value={val?.wmm ?? 40}
                onChange={(e) => {
                  const w = Number(e.target.value) || 0;
                  update(f.key, { ...(val || {}), wmm: w });
                }}
                disabled={isRO}
              />
            </div>
            <div>
              <label className="form-label">Hauteur (mm)</label>
              <input
                type="number"
                className="form-input"
                value={val?.hmm ?? 20}
                onChange={(e) => {
                  const h = Number(e.target.value) || 0;
                  update(f.key, { ...(val || {}), hmm: h });
                }}
                disabled={isRO}
              />
            </div>
          </div>

          {val?.fileName && (
            <small className="form-help">
              Fichier : {val.fileName}{val?.bytes ? ` - ${val.bytes.length} octets` : ""}
            </small>
          )}
        </div>
      )}

    </div>
  );
});
